import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart3, TrendingUp, Package, Clock, Percent, Paperclip, Globe, Zap
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  AreaChart, Area, PieChart, Pie, Cell, Legend
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS_STATUS = { New: '#f7a11a', Replied: '#059669', Archived: '#64748b' };
const BAR_COLORS = ['#f7a11a', '#0b2545', '#f7a11a', '#0b2545', '#f7a11a', '#0b2545', '#f7a11a', '#0b2545'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-[#0b2545] text-white px-3 py-2 shadow-xl">
      <div className="text-[10px] tracking-widest uppercase text-white/60">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="text-sm font-bold" style={{ color: p.color || '#f7a11a' }}>
          {p.name}: {p.value}
        </div>
      ))}
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, sub, color = 'bg-[#0b2545]', text = 'text-white' }) => (
  <div className={`${color} ${text} p-6 relative overflow-hidden`}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs tracking-widest uppercase opacity-80">{label}</div>
        <div className="mt-2 text-4xl font-black">{value}</div>
        {sub && <div className="mt-1 text-[11px] opacity-70">{sub}</div>}
      </div>
      <Icon className="w-6 h-6 opacity-50" />
    </div>
  </div>
);

const SectionTitle = ({ icon: Icon, title, desc }) => (
  <div className="flex items-start gap-3 mb-4">
    <div className="w-9 h-9 bg-[#f7a11a] flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-slate-900" />
    </div>
    <div>
      <div className="text-lg font-black text-slate-900">{title}</div>
      {desc && <div className="text-xs text-slate-500">{desc}</div>}
    </div>
  </div>
);

const Analytics = ({ token }) => {
  const [data, setData] = useState(null);
  const [range, setRange] = useState('weekly'); // weekly | monthly
  const [loading, setLoading] = useState(true);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API}/admin/analytics`, auth)
      .then((res) => setData(res.data))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  if (loading || !data) {
    return (
      <div className="py-24 text-center text-slate-500">
        <BarChart3 className="w-10 h-10 text-slate-300 mx-auto mb-3 animate-pulse" />
        Loading analytics...
      </div>
    );
  }

  const trend = range === 'weekly' ? data.weekly : data.monthly;
  const k = data.kpis;

  return (
    <div className="space-y-8">
      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Zap} label="Last 24 hours" value={k.last_24h} sub="new RFQs today" color="bg-[#f7a11a]" text="text-slate-900" />
        <KpiCard icon={TrendingUp} label="Last 7 days" value={k.last_7d} sub={`vs total ${k.total}`} />
        <KpiCard icon={Clock} label="Last 30 days" value={k.last_30d} sub="rolling month" color="bg-emerald-600" />
        <KpiCard icon={Percent} label="Reply rate" value={`${k.reply_rate}%`} sub={`${k.with_attachments} with docs (${k.attachment_rate}%)`} color="bg-slate-700" />
      </div>

      {/* Trend chart */}
      <div className="bg-white border border-slate-200 p-6">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <SectionTitle icon={TrendingUp} title="RFQ Volume Trend" desc={range === 'weekly' ? 'RFQs received per week (last 12 weeks)' : 'RFQs received per month (last 12 months)'} />
          <div className="flex gap-1 bg-slate-100 p-1">
            {['weekly', 'monthly'].map((r) => (
              <button key={r} onClick={() => setRange(r)} className={`px-4 py-1.5 text-xs font-bold tracking-widest uppercase transition-colors ${range === r ? 'bg-[#0b2545] text-white' : 'text-slate-600 hover:text-slate-900'}`}>
                {r}
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={trend} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
            <defs>
              <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f7a11a" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#f7a11a" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
            <YAxis tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#f7a11a', strokeWidth: 1, strokeDasharray: '3 3' }} />
            <Area type="monotone" dataKey="count" name="RFQs" stroke="#f7a11a" strokeWidth={2.5} fill="url(#trendGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top products */}
        <div className="bg-white border border-slate-200 p-6">
          <SectionTitle icon={Package} title="Top Requested Products" desc="Which products your buyers are asking for" />
          {data.top_products.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-500">No product data yet — RFQs need a Product field.</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, data.top_products.length * 40)}>
              <BarChart data={data.top_products} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fill: '#0f172a', fontSize: 12, fontWeight: 600 }} tickLine={false} axisLine={false} width={150} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(247, 161, 26, 0.08)' }} />
                <Bar dataKey="count" name="RFQs" radius={[0, 2, 2, 0]}>
                  {data.top_products.map((_, i) => (
                    <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Status pie */}
        <div className="bg-white border border-slate-200 p-6">
          <SectionTitle icon={Percent} title="RFQ Status Breakdown" desc="How your pipeline is distributed" />
          {k.total === 0 ? (
            <div className="py-12 text-center text-sm text-slate-500">No RFQs yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.status_break} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={3} label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                  {data.status_break.map((s, i) => (
                    <Cell key={i} fill={COLORS_STATUS[s.name] || '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Top countries */}
      <div className="bg-white border border-slate-200 p-6">
        <SectionTitle icon={Globe} title="Top Destination Countries" desc="Where your leads are coming from" />
        {data.top_countries.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500">No country data yet — buyers can add destination country on the RFQ form.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {data.top_countries.map((c, i) => (
              <div key={c.name} className="p-4 bg-slate-50 border border-slate-200 hover:border-[#f7a11a] transition-colors">
                <div className="text-[10px] tracking-widest uppercase text-slate-500">#{i + 1}</div>
                <div className="mt-1 font-bold text-slate-900 truncate">{c.name}</div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-2xl font-black text-[#f7a11a]">{c.count}</span>
                  <span className="text-xs text-slate-500">RFQs</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
